import { Avatar, Segmented, Select, Toggle } from '@pa/ui'
import { Search, Stethoscope } from 'lucide-react'
import { useState } from 'react'
import type { Settings, VisitType } from '../model/types'
import { useSettingsDraft } from '../SettingsDraftProvider'
import { PfTags } from './PfTags'
import { useFocusRow } from './useFocusRow'
import { WhoCanTakeModal } from './WhoCanTakeModal'

export type VisitTypeFilter = 'all' | 'on' | 'off'

const LENGTHS = [15, 20, 30, 45, 60, 90] as const

/**
 * C-06 Visit types (UserFlows C-06): search, All / On / Off, "N of M offered to patients", and per row Offer, the
 * name with its tags, Length, Providers (→ C-06a) and Default. [New] (Q-2) a Video visit switch per row.
 * `q` and `show` live in the URL (Frontend §6.1).
 */
export function VisitTypesTab({
  q,
  show,
  onSearch,
  focus,
}: {
  q: string
  show: VisitTypeFilter
  onSearch: (next: { q?: string; show?: VisitTypeFilter }) => void
  /** A row id from a C-05 arrow: scrolled to and flashed. */
  focus?: string | undefined
}) {
  const { draft, update } = useSettingsDraft()
  useFocusRow(focus)
  const [editing, setEditing] = useState<string | null>(null)
  const term = q.trim().toLowerCase()
  const rows = draft.visitTypes.filter(
    (v) =>
      (!term || v.name.toLowerCase().includes(term)) && (show === 'all' || (show === 'on' ? v.enabled : !v.enabled)),
  )
  const enabledCount = draft.visitTypes.filter((v) => v.enabled).length
  const patch = (id: string, p: Partial<VisitType>) => {
    update((d) => ({ ...d, visitTypes: d.visitTypes.map((v) => (v.id === id ? { ...v, ...p } : v)) }))
  }
  const mapped = editing ? draft.visitTypes.find((v) => v.id === editing) : undefined

  return (
    <div className="card card-fill">
      <div className="toolbar filters">
        <div className="search input-wrap">
          <Search aria-hidden />
          <input
            className="input"
            placeholder="Search visit types"
            aria-label="Search visit types"
            value={q}
            onChange={(e) => {
              onSearch({ q: e.target.value })
            }}
          />
        </div>
        <Segmented
          label="Show visit types"
          value={show}
          onChange={(v) => {
            onSearch({ show: v })
          }}
          options={[
            { value: 'all', label: 'All' },
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
        />
        <span className="hint offered-count">
          <b>{enabledCount}</b> of {draft.visitTypes.length} offered to patients
        </span>
      </div>
      <div className="table-wrap">
        <table className="table rtable set-table vt-table">
          <thead>
            <tr>
              <th scope="col">Offer</th>
              <th scope="col">Visit type</th>
              <th scope="col">Length</th>
              <th scope="col">Providers</th>
              <th scope="col">Default</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <VisitTypeRow
                key={v.id}
                v={v}
                focused={v.id === focus}
                draft={draft}
                patch={patch}
                onEditProviders={() => {
                  setEditing(v.id)
                }}
                onDefault={() => {
                  update((d) => ({ ...d, rules: { ...d.rules, defaultVisitTypeId: v.id } }))
                }}
              />
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="empty">
            <Stethoscope aria-hidden />
            <div>No visit types match</div>
          </div>
        )}
      </div>
      <div className="card-foot hint">
        <b>Length</b> is used to build open times and isn&apos;t shown to patients. <b>Default</b> is preselected for
        patients and used when a link doesn&apos;t name a visit type.
      </div>
      {mapped && (
        <WhoCanTakeModal
          name={mapped.name}
          value={mapped.providerIds}
          providers={draft.providers}
          onClose={() => {
            setEditing(null)
          }}
          onDone={(ids) => {
            patch(mapped.id, { providerIds: ids })
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

function VisitTypeRow({
  v,
  focused,
  draft,
  patch,
  onEditProviders,
  onDefault,
}: {
  v: VisitType
  /** The row a C-05 arrow points at (flashed). */
  focused: boolean
  draft: Settings
  patch: (id: string, p: Partial<VisitType>) => void
  onEditProviders: () => void
  onDefault: () => void
}) {
  const providers = (v.providerIds ?? []).flatMap((id) => draft.providers.filter((p) => p.id === id))
  const first = providers[0]
  const summary =
    v.providerIds === null
      ? 'All providers'
      : first
        ? providers.length === 1
          ? first.name
          : `${String(providers.length)} providers`
        : 'No providers'
  return (
    <tr data-row={v.id} className={`${v.enabled ? '' : 'off'} ${focused ? 'row-focus' : ''}`.trim()}>
      <td className="c-toggle">
        <Toggle
          checked={v.enabled}
          onChange={(on) => {
            patch(v.id, { enabled: on })
          }}
          label={`Offer ${v.name}`}
        />
      </td>
      <td className="c-name">
        <b id={`vt-name-${v.id}`}>{v.name}</b>
        <div className="row tag-row">
          {v.telehealth && <span className="tag">Video</span>}
          <PfTags removed={v.removedInPf} isNew={v.isNew} />
        </div>
        <span className="video-switch">
          <Toggle
            checked={v.telehealth}
            onChange={(on) => {
              patch(v.id, { telehealth: on })
            }}
            label={`Video visit: ${v.name}`}
          />
          <span aria-hidden>Video visit</span>
        </span>
      </td>
      <td className="c-len" data-label="Length">
        <Select
          label={`Length of ${v.name}`}
          value={v.durationOverrideMinutes === null ? 'default' : String(v.durationOverrideMinutes)}
          onChange={(x) => {
            patch(v.id, {
              durationOverrideMinutes: x === 'default' ? null : (Number(x) as (typeof LENGTHS)[number]),
            })
          }}
          options={[
            {
              value: 'default',
              label: `Default (${String(draft.rules.defaultDurationMinutes)} min)`,
              ...(v.pfDurationMinutes !== null && { sub: `PF suggests ${String(v.pfDurationMinutes)} min` }),
            },
            ...LENGTHS.map((d) => ({ value: String(d), label: `${String(d)} min` })),
          ]}
        />
      </td>
      <td className="c-prov" data-label="Providers">
        <button
          type="button"
          className="map-btn"
          // Named by its content ("{providers} Edit"); the row's visit type describes it.
          aria-describedby={`vt-name-${v.id}`}
          onClick={onEditProviders}
        >
          {v.providerIds === null ? (
            <span className="muted">All providers</span>
          ) : first ? (
            <>
              <span className="avatar-stack">
                {providers.map((p) => (
                  <Avatar key={p.id} name={p.name} color={p.color} size="sm" />
                ))}
              </span>
              <span>{summary}</span>
            </>
          ) : (
            <span className="err-text">No providers</span>
          )}
          <span className="map-edit">Edit</span>
        </button>
      </td>
      <td className="c-default" data-label="Default">
        <input
          type="radio"
          name="default-vt"
          className="radio"
          aria-label={`Make ${v.name} the default`}
          checked={draft.rules.defaultVisitTypeId === v.id}
          disabled={!v.enabled}
          onChange={onDefault}
        />
      </td>
    </tr>
  )
}
